/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  ADD_FAMILY_INFORMATION_FACADE_TOKEN,
  IAddFamilyInformationFacade,
} from '../../../../../../data-access/src/lib/domain/facades-models/add-family-information-facade-interface';
import { PropertiesListItemsVM } from '../../../../../../../shared/ui/src/lib/models/sysque-properties-list-vm';
import { SysqueDropdownConnectorsVM } from '../../../../../../../shared/ui/src/lib/models/sysque-dropdown-connectors-vm';
import { AddFamilyInformationReturnCodes } from '../../../models/family-information-model';
import { GlobalConstants } from '../../../constants/global-constants';
import { SysqueNumberBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-number-box-vm';
import { filter, Subscription, take } from 'rxjs';
import { AcrylicSurfaceService } from '../../../acrylic-surface/acrylic-surface.service';
import {
  DeviceEquipmentDetailsDataToApply,
  DeviceEquipmentDetailsInputData,
  DeviceEquipmentDetailsReturnCodes,
  DisplayUnit,
} from '../../../models/device-equipment-details-return-codes';
import { AlertStatus, IAlert } from '../../../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-device-equipment-family-information',
  templateUrl: './device-equipment-family-information.component.html',
  styleUrl: './device-equipment-family-information.component.scss',
  standalone: false
})
export class DeviceEquipmentFamilyInformation implements OnInit, OnDestroy {
  public alertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public title = '';
  public image = '';
  public propertiesListItems: PropertiesListItemsVM[] = [];
  public isadditionalWireLengthVisible: boolean = true;
  public additionalWireLengthSpinButtons: boolean = true;
  public additionalWireLengthItems: SysqueNumberBoxVM[] = [];
  public connectorsTitle: string;
  public connectorsItems: SysqueDropdownConnectorsVM[] = [];
  public isSupportDisciplineVisible: boolean = false;
  public supportDisciplineLabel: string;
  public supportDiscipline: string;
  public fileName = '';
  private validSelectionSubscription: Subscription;
  public itemData: DeviceEquipmentDetailsInputData;
  public itemToApply: DeviceEquipmentDetailsDataToApply;
  private selectedPathSubscription: Subscription;
  private openPdfSubscription: Subscription;
  public isAlertVisible = false;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  private alertHeight = 720;
  private withoutAlertHeight = 650;

  constructor(
    @Inject(ADD_FAMILY_INFORMATION_FACADE_TOKEN) private addFamilyInformationFacade: IAddFamilyInformationFacade,
    private acrylicSurfaceService: AcrylicSurfaceService
  ) {}

  public ngOnInit(): void {
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
    this.getDataForInit();
  }

  public ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
    if (this.validSelectionSubscription) {
      this.validSelectionSubscription.unsubscribe();
    }
    if (this.selectedPathSubscription) {
      this.selectedPathSubscription.unsubscribe();
    }
    if (this.openPdfSubscription) {
      this.openPdfSubscription.unsubscribe();
    }
  }

  private getDataForInit(): void {
    this.addFamilyInformationFacade.registerControllers();
    this.validSelectionSubscription = this.addFamilyInformationFacade
      .validateAndGetData().pipe(
      filter(data => !!data), // Ensure that data is not empty or initial state
        take(1))
      .subscribe((data) => {
        this.itemData = data;
        this.itemToApply = new DeviceEquipmentDetailsDataToApply();
        this.itemToApply.discipline = this.itemData.discipline;
        this.itemToApply.partType = this.itemData.partType;
        this.itemToApply.category = this.itemData.category;
        this.initData();
        this.validateSelection();
      });
  }

  private validateSelection(): void {
    if (this.itemData.returnCode === DeviceEquipmentDetailsReturnCodes.ValidSelection) {
      this.hideAlert();
    } else if (this.itemData.returnCode === DeviceEquipmentDetailsReturnCodes.SelectionMismatchEquipment) {
      this.showAlert(GlobalConstants.SelectionMismatchEquipmentBody, AlertStatus.Warning);
    } else if (this.itemData.returnCode === DeviceEquipmentDetailsReturnCodes.FamilyNotExists) {
      this.showAlert(GlobalConstants.FamilyNotExistsBody, AlertStatus.Warning);
    }
  }

  //#region data initialization
  private initData(): void {
    this.titleInit();
    this.propertiesInit();
    this.addtionalWireLengthInit();
    this.connectorsInit();
    this.pdfLocationInit();
  }

  private titleInit(): void {
    this.title = 'Add Device/Equipment Details';
  }

  private propertiesInit(): void {
    if (!this.itemData) {
      console.error('itemData is undefined');
      return;
    }
    this.propertiesListItems = [
      {
        label: GlobalConstants.DISCIPLINE_LABEL,
        value: this.itemData.discipline ?? '',
        isEditable: true,
      },
      {
        label: GlobalConstants.PART_TYPE_LABEL,
        value: this.itemData.partType ?? '',
        isEditable: true,
      },
      {
        label: GlobalConstants.CATEGORY_LABEL,
        value: this.itemData.category ?? '',
        isEditable: true,
      },
      {
        label: GlobalConstants.MANUFACTURER_LABEL,
        value: '',
        isEditable: true,
      },
      {
        label: GlobalConstants.PART_ID_LABEL,
        value: '',
        isEditable: true,
      },
      {
        label: GlobalConstants.NOMINAL_SIZE_LABEL,
        value: '',
        isEditable: true,
      },
    ];
  }

  private addtionalWireLengthInit(): void {
    this.additionalWireLengthItems = [
      {
        label: GlobalConstants.ADDED_WIRE_LENGTH_LABEL,
        value: Number('0'),
        isMetric: this.isMetric,
        height: '30',
        width: '122',
        minValue: 0
      },
    ];
  }

  private connectorsInit(): void {
    this.connectorsTitle = '';
    const connectors = this.addFamilyInformationFacade.getDeviceEquipmentConnectors();
    const connectorsCount = this.itemData.connectorCount;
    for (let i = 0; i < connectorsCount; i++) {
      this.connectorsItems.push({
        label: '',
        data: connectors.map((x) => x.connector),
        placeholder: '',
        selectedValue: connectors[0].connector,
      });
    }
  }

  private pdfLocationInit(): void {
    this.fileName = '';
  }

  //#endregion

  public onPropertiesValueChanged($event: { value: string; label: string }): void {
    switch ($event.label) {
      case GlobalConstants.DISCIPLINE_LABEL:
        this.itemToApply.discipline = $event.value;
        break;
      case GlobalConstants.PART_TYPE_LABEL:
        this.itemToApply.partType = $event.value.toString();
        break;
      case GlobalConstants.CATEGORY_LABEL:
        this.itemToApply.category = $event.value.toString();
        break;
      case GlobalConstants.MANUFACTURER_LABEL:
        this.itemToApply.manufacturer = $event.value;
        break;
      case GlobalConstants.PART_ID_LABEL:
        this.itemToApply.model = $event.value.toString();
        break;
      case GlobalConstants.NOMINAL_SIZE_LABEL:
        this.itemToApply.nominalSize = $event.value.toString();
        break;
    }
  }

  public onAdditionalWireLengthValueChanged($event: { value: string; label: string }): void {
    this.itemToApply.addedWireLength = $event.value;
  }

  public onAttachFileClicked(): void {
    this.selectedPathSubscription = this.addFamilyInformationFacade.getPdfLocationPath().subscribe((data) => {
      this.fileName = data.split('\\').pop()?.split('/').pop() || '';
      this.itemToApply.pdfLocation = data;
    });
  }

  public onOpenPdf(): void {
    this.openPdfSubscription = this.addFamilyInformationFacade
      .openPdfFile(this.itemToApply.pdfLocation)
      .subscribe((response) => {
        if (response == AddFamilyInformationReturnCodes.PdfDoesNotExist) {
          this.showAlert(GlobalConstants.PDF_FILE_DOES_NOT_EXIST, AlertStatus.Warning);
        }
      });
  }

  public onConnectorValueChanged($event: { value: string; label: string; index: number }): void {
    let selectedConnectors;
    if(this.itemToApply.connectors === null) {
      selectedConnectors = [];
    } else {
      selectedConnectors = this.itemToApply.connectors.split(' x ').map((x) => x.trim());
    }
    selectedConnectors[$event.index] = $event.value;
    this.itemToApply.connectors = selectedConnectors.join(' x ');
  }

  public doneClick(): void {
    if (!this.validateInput()) {
      return;
    }

    this.addFamilyInformationFacade.applyDeviceEquipmentDetails(this.itemToApply);
    this.addFamilyInformationFacade.sendClosePopupAction();
    this.acrylicSurfaceService.hide();
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === GlobalConstants.DeviceEquipmentDetailsApplyDataResponse) {
      if (event.newValue === '') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response == DeviceEquipmentDetailsReturnCodes.Success) {
        this.addFamilyInformationFacade.sendClosePopupAction();
      }
    }
  }

  public validateInput(): boolean {
    if (this.itemToApply.addedWireLength === '') {
      this.showAlert(GlobalConstants.ENTER_ADDED_WIRE_LENGTH, AlertStatus.Warning);
      return false;
    }
    if (this.itemToApply.manufacturer === '') {
      this.showAlert(GlobalConstants.ENTER_MANUFACTURER_NAME, AlertStatus.Warning);
      return false;
    }
    if (this.itemToApply.model === '') {
      this.showAlert(GlobalConstants.ENTER_PART_ID, AlertStatus.Warning);
      return false;
    }
    if (this.itemToApply.nominalSize === '') {
      this.showAlert(GlobalConstants.ENTER_NOMINAL_SIZE, AlertStatus.Warning);
      return false;
    }
    return true;
  }

  //#region alert
  private showAlert(message: string, alertStatus: AlertStatus): void {
    this.addFamilyInformationFacade.windowResize(this.alertHeight);
    this.warningMessage = message;
    this.alertStatus = alertStatus;
    this.isAlertVisible = true;
  }

  public hideAlert(): void {
    this.addFamilyInformationFacade.windowResize(this.withoutAlertHeight);
    this.warningMessage = '';
    this.isAlertVisible = false;
  }

  //#endregion

  private get isMetric(): boolean {
    return this.itemData.unit == DisplayUnit.Metric;
  }
}
